import {
  IAdvertiser,
  IAdvertiserCategory,
  IAdvertiserControlOption,
  IAdvertiserGridRow,
  IAdvertiserResponseItem,
} from '@/Interfaces/Advertiser.interface';
import { IPaginatedApiResponse } from '@/Interfaces/ApiResponse/PaginatedApiResponse.interface';
import { IClientControlOption, IControlOption, IWhitelabelFilterOption } from '@/Interfaces/ControlOption.interface';
import { getControlOptionsValues } from '@/helpers/filters/filterMapping';
import { SearchAdvertisersFilters } from '@/types/advertisers.types';
import { getSortFilter } from '../grid/getSortFilter';

//#region id
export function getAdvertiserIdsFromAdvertisers<
  TAdvertiser extends IControlOption = IControlOption,
  TAdvertiserKey extends keyof TAdvertiser = keyof TAdvertiser,
>(advertisers: TAdvertiser[] = [], advertiserKey: TAdvertiserKey): TAdvertiser[TAdvertiserKey][] {
  return advertisers.map(advertiser => advertiser[advertiserKey]).filter(Boolean);
}

export function getAdvertiserIdsFromClients<
  TAdvertiser extends IControlOption = IControlOption,
  TAdvertiserKey extends keyof TAdvertiser = keyof TAdvertiser,
>(clients: IClientControlOption<TAdvertiser>[] = [], advertiserKey: TAdvertiserKey): TAdvertiser[TAdvertiserKey][] {
  return clients.flatMap(client => getAdvertiserIdsFromAdvertisers(client?.advertisers || [], advertiserKey));
}

export function getAdvertiserIdsFromWhitelabels<
  TAdvertiser extends IControlOption = IControlOption,
  TAdvertiserKey extends keyof TAdvertiser = keyof TAdvertiser,
>(
  whitelabels: IWhitelabelFilterOption<TAdvertiser>[] = [],
  advertiserKey: TAdvertiserKey,
): TAdvertiser[TAdvertiserKey][] {
  return whitelabels.flatMap(wl => getAdvertiserIdsFromClients(wl?.clients || [], advertiserKey));
}

export function getHighestLevelAdvertisers<
  TAdvertiser extends IControlOption = IControlOption,
  TKey extends keyof TAdvertiser = keyof TAdvertiser,
>(
  whitelabels: IWhitelabelFilterOption<TAdvertiser>[] = [],
  clients: IClientControlOption<TAdvertiser>[] = [],
  advertisers: TAdvertiser[] = [],
  advertiserKey: TKey,
) {
  if (advertisers?.length) {
    return getAdvertiserIdsFromAdvertisers<TAdvertiser, TKey>(advertisers, advertiserKey);
  }
  if (clients?.length) {
    return getAdvertiserIdsFromClients<TAdvertiser, TKey>(clients, advertiserKey);
  }
  if (whitelabels?.length) {
    return getAdvertiserIdsFromWhitelabels<TAdvertiser, TKey>(whitelabels, advertiserKey);
  }

  return [];
}
//#endregion

//#region advertisers
export const getAdvertisersFromClients = (clients: IClientControlOption[] = []): IControlOption[] => {
  return clients.flatMap(client => (client?.advertisers || []) as IControlOption[]);
};

export const getAdvertisersFromWhitelabels = (whitelabels: IWhitelabelFilterOption[] = []): IControlOption[] => {
  return whitelabels.flatMap(wl => getAdvertisersFromClients(wl?.clients || []));
};

export const getUniqueAdvertisers = <
  TAdvertiser extends IControlOption = IControlOption,
  TAdvertiserKey extends keyof TAdvertiser = keyof TAdvertiser,
>(
  advertisers: TAdvertiser[],
  advertiserKey: TAdvertiserKey = 'value' as TAdvertiserKey,
): TAdvertiser[] => {
  const labelCount = advertisers.reduce(
    (acc, advertiser) => {
      acc[advertiser.label] = (acc[advertiser.label] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const uniqueAdvertisersMap = new Map<string, TAdvertiser>();
  advertisers.forEach(advertiser => {
    const key = String(advertiser[advertiserKey]);
    const isDuplicate = labelCount[advertiser.label] > 1;

    uniqueAdvertisersMap.set(key, {
      ...advertiser,
      label: isDuplicate ? `${advertiser.label} (#${advertiser[advertiserKey]})` : advertiser.label,
    });
  });

  return Array.from(uniqueAdvertisersMap.values()).sort((a, b) => a.label.localeCompare(b.label));
};

export const formatAdvertisersResponseData = ({
  data,
  meta,
}: IPaginatedApiResponse<IAdvertiserResponseItem[]>): { data: IAdvertiserGridRow[]; total: number } => {
  return {
    data: data.map(advertiser => ({
      id: advertiser.id,
      name: advertiser.name,
      isActive: advertiser.is_active,
      isArchived: advertiser.is_archived,
      domain: advertiser.domain || null,
      alternativeId: advertiser.alternative_id || null,
      createdAt: advertiser.created_at,
      campaignsCount: advertiser.campaignsCount,
      client: advertiser.client
        ? {
            value: advertiser.client.id,
            label: advertiser.client.name,
            agencies:
              advertiser.client.agencies && advertiser.client.agencies.length > 0
                ? advertiser.client.agencies?.map(agency => ({
                    value: agency.id,
                    label: agency.name,
                  }))
                : [],
          }
        : null,
      agency: advertiser.agency
        ? {
            value: advertiser.agency.id,
            label: advertiser.agency.name,
          }
        : null,
      category: advertiser.category || null,
    })),
    total: meta.total,
  };
};

export const formatCategoriesResponseData = (categories: IAdvertiserCategory[]): IControlOption<string, string>[] => {
  if (!categories?.length) return [];
  return categories
    .map(category => {
      const { key, name } = category;

      return {
        value: key,
        label: name,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const transformAdvertiserFilters = (filters: SearchAdvertisersFilters) => {
  const { whitelabels, clients, sort, ...payload } = filters;
  return {
    ...payload,
    sort: getSortFilter(sort),
    whitelabelIds: getControlOptionsValues(whitelabels),
    clientIds: getControlOptionsValues(clients),
  };
};

//#endregion

export function sortAdvertisersByLabels<AType extends IControlOption>(advertisers: AType[]): AType[] {
  return [...advertisers].sort((a, b) => (a.label || '').trim().localeCompare((b.label || '').trim())) || [];
}

export const getAdvertiserControlOption = (
  advertiser: Pick<IAdvertiser, 'id' | 'name' | 'beeswaxAdvertiser'>,
): IAdvertiserControlOption => {
  return {
    value: advertiser.id,
    label: advertiser.name,
    beeswaxId: advertiser.beeswaxAdvertiser?.beeswax_id,
  };
};
